import React, { useState, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { getApiToken } from "../authUtils";
import urlconfig from "../config/urlconfig";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  TablePagination,
  LinearProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";

const STATUS_META = {
  NEW: { label: "New", color: "success" },
  DUPLICATE_IN_DB: { label: "Already in DB", color: "warning" },
  DUPLICATE_IN_FILE: { label: "Duplicate in File", color: "warning" },
  INVALID: { label: "Invalid", color: "error" },
};

const BulkVendorImport = ({ onImported }) => {
  const { instance, accounts } = useMsal();
  const account = accounts?.[0];
  const fileInputRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const sendAuthenticatedRequest = async (url, method, body, isFormData = false) => {
    const token = await getApiToken(instance, account);
    const headers = { Authorization: `Bearer ${token}` };
    if (!isFormData) headers["Content-Type"] = "application/json";
    const res = await fetch(url, {
      method,
      headers,
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
    });
    const text = await res.text();
    if (!res.ok) throw new Error(text || "API failed");
    try {
      return text ? JSON.parse(text) : {};
    } catch {
      return text;
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setResult(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const data = await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/vendors/bulk-import/preview`,
        "POST",
        formData,
        true
      );
      setRows(data || []);
      // Default: only auto-select rows that are genuinely new
      setSelected(new Set(data.filter((r) => r.status === "NEW").map((r) => r.rowNumber)));
      setPage(0);
    } catch (err) {
      setError(err.message || "Failed to parse file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const stats = rows.reduce(
    (acc, r) => {
      acc.total++;
      acc[r.status] = (acc[r.status] || 0) + 1;
      return acc;
    },
    { total: 0 }
  );

  const filteredRows = rows.filter((r) => filter === "all" || r.status === filter);
  const paginatedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const toggleRow = (rowNumber) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    const filteredEligible = filteredRows.filter((r) => r.status === "NEW" || r.status === "DUPLICATE_IN_FILE");
    const allSelected = filteredEligible.every((r) => selected.has(r.rowNumber));
    setSelected((prev) => {
      const next = new Set(prev);
      filteredEligible.forEach((r) => (allSelected ? next.delete(r.rowNumber) : next.add(r.rowNumber)));
      return next;
    });
  };

  const handleImport = async () => {
    const toImport = rows.filter((r) => selected.has(r.rowNumber));
    if (toImport.length === 0) return;
    setImporting(true);
    setError(null);
    try {
      const res = await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/vendors/bulk-import/commit`,
        "POST",
        toImport
      );
      setResult(res);
      setRows([]);
      setSelected(new Set());
      onImported?.();
    } catch (err) {
      setError(err.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const downloadCsvTemplate = () => {
    const headers = [
      "legalVendorName",
      "vendorAddress",
      "vendorState",
      "vendorGST",
      "cScmPoc",
      // Add contact4Person / contact4Email / contact4Phone (up to contact10) for more contacts
      ...[1, 2, 3].flatMap((n) => [`contact${n}Person`, `contact${n}Email`, `contact${n}Phone`]),
    ];

    const csvContent = headers.join(",") + "\n";

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "vendors_import_template.csv");

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Bulk Import Legal Vendors
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload a CSV with columns: legalVendorName, vendorAddress, vendorState, vendorGST, cScmPoc, then one
        contact1Person / contact1Email / contact1Phone group per contact (contact2…, up to contact10). Older
        files with vendorSpoc / vendorSpocNumber still work as a single contact. vendorGST is optional, but vendors
        without it can't be picked on cost sheets. Duplicates (matched by GST or name) and invalid contacts are
        flagged for review — nothing is saved until you approve.
      </Typography>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        hidden
        onChange={handleFileSelect}
      />
      <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? "Parsing…" : "Choose CSV File"}
        </Button>

        <Button
          variant="text"
          startIcon={<DownloadIcon />}
          onClick={downloadCsvTemplate}
        >
          Download CSV Template
        </Button>
      </Box>

      {uploading && <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {result && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setResult(null)}>
          Imported {result.imported} vendor{result.imported === 1 ? "" : "s"}
          {result.skipped > 0 ? ` · Skipped ${result.skipped}` : ""}.
          {result.errors?.length > 0 && (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {result.errors.map((e, i) => (
                <li key={i}>
                  <Typography variant="caption">{e}</Typography>
                </li>
              ))}
            </Box>
          )}
        </Alert>
      )}

      {rows.length > 0 && (
        <>
          {/* Stats */}
          <Box sx={{ display: "flex", gap: 2, mt: 3, mb: 2, flexWrap: "wrap" }}>
            {[
              { label: "Total Rows", value: stats.total, color: "text.primary" },
              { label: "New", value: stats.NEW || 0, color: "success.main" },
              { label: "Duplicate in DB", value: stats.DUPLICATE_IN_DB || 0, color: "warning.main" },
              { label: "Duplicate in File", value: stats.DUPLICATE_IN_FILE || 0, color: "warning.main" },
              { label: "Invalid", value: stats.INVALID || 0, color: "error.main" },
            ].map((s) => (
              <Box key={s.label} sx={{ minWidth: 130, p: 1.5, bgcolor: "#f8fafc", borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={800} sx={{ color: s.color }}>
                  {s.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Filter */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
            <ToggleButtonGroup
              value={filter}
              exclusive
              size="small"
              onChange={(e, val) => {
                if (val) {
                  setFilter(val);
                  setPage(0);
                }
              }}
            >
              <ToggleButton value="all">All ({stats.total})</ToggleButton>
              <ToggleButton value="NEW">New ({stats.NEW || 0})</ToggleButton>
              <ToggleButton value="DUPLICATE_IN_DB">Dup. in DB ({stats.DUPLICATE_IN_DB || 0})</ToggleButton>
              <ToggleButton value="DUPLICATE_IN_FILE">Dup. in File ({stats.DUPLICATE_IN_FILE || 0})</ToggleButton>
              <ToggleButton value="INVALID">Invalid ({stats.INVALID || 0})</ToggleButton>
            </ToggleButtonGroup>

            <Button size="small" onClick={toggleSelectAllFiltered}>
              Toggle select all (filtered)
            </Button>
          </Box>

          <TableContainer sx={{ maxHeight: 480, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ bgcolor: "#f8fafc" }} />
                  <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: 700 }}>Legal Vendor Name</TableCell>
                  <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: 700 }}>GST</TableCell>
                  <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: 700 }}>State</TableCell>
                  <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: 700 }}>Contacts</TableCell>
                  <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: 700 }}>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row) => {
                  const meta = STATUS_META[row.status] || { label: row.status, color: "default" };
                  const selectable = row.status === "NEW" || row.status === "DUPLICATE_IN_FILE";
                  return (
                    <TableRow key={row.rowNumber} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.has(row.rowNumber)}
                          disabled={!selectable}
                          onChange={() => toggleRow(row.rowNumber)}
                        />
                      </TableCell>
                      <TableCell>{row.rowNumber}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.legalVendorName || "—"}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{row.vendorGST || "—"}</TableCell>
                      <TableCell>{row.vendorState || "—"}</TableCell>
                      <TableCell>
                        {row.contacts?.length
                          ? row.contacts.map((c, i) => (
                              <Typography key={i} variant="body2" sx={{ whiteSpace: "nowrap" }}>
                                {[c.contactPerson || "—", c.phone, c.email].filter(Boolean).join(" · ")}
                              </Typography>
                            ))
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={meta.label} color={meta.color} variant={meta.color === "default" ? "outlined" : "filled"} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {row.errorMessage || ""}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 250]}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <PlaylistAddCheckIcon />}
              disabled={importing || selected.size === 0}
              onClick={handleImport}
            >
              {importing ? "Importing…" : `Import Selected (${selected.size})`}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default BulkVendorImport;